import {toRaw, watch} from 'vue';

import settings from '@/config/settings';

import type {IKeycloakUser} from '@/assets/keycloak/types';

import {keycloakAPI} from '@/assets/keycloak';

export type WorkerListener = (message: unknown) => void;

type WorkerConfig = {
  backendUrl: string;
};

let iframeEl: HTMLIFrameElement | null = null;
let iframeReady = false;
let bootstrapped = false;
let lastSentConfig: WorkerConfig | null = null;
let lastSentToken: string | null = null;
let lastSentIsAuthenticated: boolean | null = null;

const listeners = new Set<WorkerListener>();

const fanout = (message: unknown) => {
  listeners.forEach((cb) => {
    try {
      cb(message);
    } catch (error) {
      console.error('Worker message listener error:', error);
    }
  });
};

const postToIframe = (payload: Record<string, unknown>) => {
  if (!iframeEl?.contentWindow) return;
  iframeEl.contentWindow.postMessage({...payload, timestamp: Date.now()}, '*');
};

const ensureIframe = () => {
  if (iframeEl) return iframeEl;

  const iframe = document.createElement('iframe');
  iframe.src = settings.SERVICES.WORKER.IFRAME_URL;
  iframe.name = 'notifier-host';
  iframe.style.display = 'none';
  iframe.setAttribute('aria-hidden', 'true');

  iframe.addEventListener('load', () => {
    iframeReady = true;
    resendState();
  });

  document.body.appendChild(iframe);
  iframeEl = iframe;
  return iframe;
};

const sendConfigToIframe = () => {
  const next: WorkerConfig = {
    backendUrl: settings.SERVICES.BASE.URL,
  };

  if (lastSentConfig && lastSentConfig.backendUrl === next.backendUrl) {
    return;
  }

  lastSentConfig = next;
  postToIframe({type: 'UPDATE_CONFIG', config: next});
};

const sendTokenToIframe = (token: string | null) => {
  if (!token || token === lastSentToken) return;
  lastSentToken = token;
  postToIframe({type: 'UPDATE_TOKEN', token});
};

const sendUserInfoToIframe = (userInfo: IKeycloakUser) => {
  const plainUserInfo = JSON.parse(JSON.stringify(userInfo));
  postToIframe({type: 'UPDATE_USER_INFO', userInfo: plainUserInfo});
};

const sendAuthChangeToIframe = (isAuthenticated: boolean) => {
  if (lastSentIsAuthenticated === isAuthenticated) return;
  lastSentIsAuthenticated = isAuthenticated;
  postToIframe({type: 'AUTH_CHANGE', isAuthenticated});
};

const resendState = () => {
  if (!iframeReady) return;

  const isAuthenticated = keycloakAPI.getIsLoggedIn.value;
  const token = keycloakAPI.getToken.value;

  lastSentIsAuthenticated = null;
  lastSentToken = null;
  lastSentConfig = null;

  if (token) {
    sendTokenToIframe(token);
    sendUserInfoToIframe(toRaw(keycloakAPI.getUserInfo.value));
  }
  sendAuthChangeToIframe(isAuthenticated);
  if (isAuthenticated) {
    bootstrapped = true;
    sendConfigToIframe();
  }
};

const bootstrap = () => {
  ensureIframe();

  if (bootstrapped) return;
  bootstrapped = true;

  const isAuthenticated = keycloakAPI.getIsLoggedIn.value;
  const token = keycloakAPI.getToken.value;
  const sendConfig = () => {
    if (!isAuthenticated) return;
    if (token) sendTokenToIframe(token);
    sendConfigToIframe();
  };

  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'default'
  ) {
    Notification.requestPermission().then(sendConfig);
    return;
  }

  sendConfig();
};

const isFromIframe = (event: MessageEvent) =>
  iframeEl !== null && event.source === iframeEl.contentWindow;

const attachMessageListener = () => {
  window.addEventListener('message', (event) => {
    if (!isFromIframe(event)) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    if (
      data.type === 'NEW_NOTIFICATIONS' ||
      data.type === 'UNREAD_COUNT' ||
      data.type === 'NOTIFICATION_ACTION'
    ) {
      fanout(data);
    }
  });
};

export const onWorkerMessage = (cb: WorkerListener) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

export const initMainAppWorker = () => {
  attachMessageListener();

  watch(
    () => keycloakAPI.getIsLoggedIn.value,
    (isLoggedIn) => {
      sendAuthChangeToIframe(isLoggedIn);
      if (!isLoggedIn) {
        bootstrapped = false;
        return;
      }
      bootstrap();
    },
    {immediate: true},
  );

  watch(
    () => keycloakAPI.getToken.value,
    (newToken) => {
      sendTokenToIframe(newToken);
      if (newToken) {
        sendUserInfoToIframe(toRaw(keycloakAPI.getUserInfo.value));
      }
    },
    {immediate: true},
  );

  return {
    getWorker: () => null,
  };
};
